import React, { useCallback, useRef } from 'react';
import { buildRecordsUrls, playingComponent, videoOrigins } from '../sessionBroadcastUtils';
import BizzaPlyr from '@bizzabo/components/BizzaPlyr';
import { Preroll as PrerollCommon, MutedPreviewOverlay, MuxPlayerWrapper as MuxPlayer } from '@bizzabo/broadcast';
import FeatureToggleUtils from 'FeatureToggleUtils';
import { VolumeMuteOutlineIcon } from '@bizzabo/icons';

const initialPlayerConfigs = {
    controls: ['play-large', 'mute', 'volume', 'captions'],
    settings: ['quality'],
    clickToPlay: false,
    muted: false,
    storage: { enabled: false },
    keyboard: { focused: false, global: false },
};

const Preroll = (
    {
        videoUrl: rawRecordUrl,
        setPrerollVideoStartedPlaying,
        availableHls: hlsEnabled = false,
        overridePlayerConfigs = {},
    }
) => {
    // TODO: check imported Preroll compoenent and use it instead local players
    const isPrerollCommonEnabled = FeatureToggleUtils.isFeatureEnabled('broadcastPlayerPrerollCommon');

    const versionToScriptUrls= {
        plyr: FeatureToggleUtils.getFeatureValue('bap-plyr-io-version'),
        hls: FeatureToggleUtils.getFeatureValue('bap-hls-js-version'),
        mux: FeatureToggleUtils.getFeatureValue('bap-mux-player-version'),
    };
    const bizzaboAgnosticPlayer = FeatureToggleUtils.getFeatureValue('bizzabo-agnostic-player');

    const isMuxPlayer = bizzaboAgnosticPlayer === 'mux';

    if (isPrerollCommonEnabled) {
        return (
            <PrerollCommon
                videoUrl={rawRecordUrl}
                availableHls={hlsEnabled}
                overridePlayerConfigs={overridePlayerConfigs}
                videoOriginsCloudinary={videoOrigins.CLOUDINARY}
                playingComponentPreroll={playingComponent.Preroll}
                loadingIconHtml={<i className="preroll-video-spinner fa fa-spinner fa-spin fa-2x"/>}
                setPrerollVideoStartedPlaying={setPrerollVideoStartedPlaying}
                versionToScriptUrls={versionToScriptUrls}
                bizzaboAgnosticPlayer={bizzaboAgnosticPlayer}
            />
        );
    }
    const playerConfigs = { ...initialPlayerConfigs, ...overridePlayerConfigs };

    let videoOrigin = undefined;
    if (rawRecordUrl?.includes('cloudinary.com')) {
        videoOrigin = videoOrigins.CLOUDINARY;
    } else if (rawRecordUrl?.includes('kaltura.com')) {
        videoOrigin = videoOrigins.KALTURA;
    }
    const recordUrls = buildRecordsUrls({
        rawRecordUrl, hlsEnabled, videoOrigin, context: playingComponent.Preroll
    });

    const videoElRef = useRef(null);

    const autoplayFailedCallback = useCallback(() => {
        videoElRef.current?.addEventListener('playing', () => {
            setPrerollVideoStartedPlaying(true);
        });
    }, []);

    const autoplaySuccessCallback = () => setPrerollVideoStartedPlaying(true);

    const videoInitProps = {
        videoElRef,
        recordUrls,
        hlsEnabled,
        autoplayFailedCallback,
        autoplaySuccessCallback,
        playerConfigs,
        MutedOverlayComponent: <MutedPreviewOverlay IconComponent={VolumeMuteOutlineIcon}/>,
        versionToScriptUrls,
    };

    if (isMuxPlayer) {
        return (
            <MuxPlayer
                {...videoInitProps}
                streamType={'live'}
                autoplay
            >
                {
                    ({
                        isPlayerLoading,
                        streamType,
                        videoElRef,
                        videoPlayerCls,
                        isPlayerLoaded,
                        MuxPlayerWrapper,
                        src,
                        autoplay,
                        startTime,
                        metadata,
                        isApplyClassicTheme,
                    }) => (
                <>
                    {isPlayerLoading &&
                        <i className="preroll-video-spinner fa fa-spinner fa-spin fa-2x" />}
                    <MuxPlayerWrapper
                        ref={videoElRef}
                        className={videoPlayerCls}
                        style={{ display: isPlayerLoaded ? 'block' : 'none' }}
                        src={src}
                        autoplay={autoplay}
                        startTime={startTime}
                        metadata={metadata}
                        preroll
                        streamType={streamType}
                        isApplyClassicTheme={isApplyClassicTheme}
                    />
                </>
                    )
                }
            </MuxPlayer>
        );
    }
    return (
        <BizzaPlyr {...videoInitProps}>
            {
                ({ isPlayerLoading, videoElRef, videoPlayerCls, isPlayerLoaded, PlyrVideoElementWrapper }) => (
                    <>
                        {
                            isPlayerLoading &&
                            <i className="preroll-video-spinner fa fa-spinner fa-spin fa-2x" />
                        }
                        <PlyrVideoElementWrapper
                            ref={videoElRef}
                            playsInline
                            className={videoPlayerCls}
                            style={{ display: isPlayerLoaded ? 'block' : 'none' }}
                        />
                    </>
                )
            }
        </BizzaPlyr>
    );
};

export default Preroll;
